import { AnswerBox } from '@/components/seo/answer-box';
import { ChooseCards, ComparisonTable, usLabelOf } from '@/components/seo/comparison-table';
import { faqJsonLd, FaqSection } from '@/components/seo/faq-list';
import { breadcrumbJsonLd, FinalCta, SeeAlso, SectionTitle, SeoSection } from '@/components/seo/seo-blocks';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { COMPARISON_PAGES, findComparisonPage, FREE_VS_PAID } from '@/content/seo/comparisons';
import { pageTitle } from '@/lib/seo/meta';
import { Href, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';
import NotFoundScreen from '../+not-found';

export async function generateStaticParams(): Promise<{ slug: string }[]> {
  return COMPARISON_PAGES.map((page) => ({ slug: page.slug }));
}

const formatDate = (iso: string) => iso.split('-').reverse().join('/');

/** Destino de "Comparar" no caminho: a lista de comparações do hub. */
const COMPARE_HUB = '/guias#comparacoes';

/**
 * Comparativo (`/comparar/[slug]`): resposta direta, lado a lado em `<table>`
 * (com o que é pago marcado e a frase do grátis embaixo), "Fique com X se" em
 * destaque e FAQ. Conteúdo em `content/seo/comparisons.ts`, que nunca cita
 * nome de outro produto.
 */
export default function ComparisonScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const page = findComparisonPage(String(slug ?? ''));

  if (!page) return <NotFoundScreen />;

  const path = `/comparar/${page.slug}`;
  const others = COMPARISON_PAGES.filter((item) => item.slug !== page.slug);
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.h1,
      description: page.description,
      inLanguage: 'pt-BR',
      image: `${SITE_URL}/api/og-image`,
      datePublished: page.publishedAt,
      dateModified: page.checkedAt,
      mainEntityOfPage: `${SITE_URL}${path}`,
      author: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Controlapag' },
      publisher: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Controlapag' },
    },
    faqJsonLd(page.faq),
    breadcrumbJsonLd(SITE_URL, [
      { name: 'Início', path: '/' },
      { name: 'Comparar', path: COMPARE_HUB },
      { name: page.h1, path },
    ]),
  ];

  return (
    <SeoPageShell
      screenTitle={page.h1}
      dockLabel="Clientes, agenda e cobranças num lugar só"
      head={<SeoHead title={pageTitle(page.title)} description={page.description} path={path} jsonLd={jsonLd} />}
    >
      <SeoHero
        width="narrow"
        crumbs={[
          { label: 'Início', href: '/' },
          { label: 'Comparar', href: COMPARE_HUB as Href },
        ]}
        meta={`Por Equipe Controlapag · conferido em ${formatDate(page.checkedAt)}`}
        title={page.h1}
      >
        <View className="mx-auto w-full max-w-4xl">
          <AnswerBox text={page.answer} />
        </View>
      </SeoHero>

      <SeoSection>
        <View className="mx-auto w-full max-w-4xl">
          <SectionTitle eyebrow="Lado a lado" title={`${usLabelOf(page)} ou ${page.otherWithArticle}`} />
          <ComparisonTable page={page} />
          <ThemedText className="mt-3 text-sm text-neutral-500">
            Comparação feita em {formatDate(page.checkedAt)}, com base no que cada opção oferece por padrão.{' '}
            {FREE_VS_PAID}
          </ThemedText>
          <View className="mt-8">
            <ChooseCards page={page} />
          </View>
        </View>
      </SeoSection>

      <FaqSection
        items={page.faq}
        footer={<SeeAlso links={others.map((item) => ({ label: item.h1, href: `/comparar/${item.slug}` as Href }))} />}
      />

      <FinalCta title="Crie sua conta grátis e conheça" />
    </SeoPageShell>
  );
}
